import { useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/cn.js';
import { useFocusTrap } from '../lib/useFocusTrap.js';
import { token } from '../theme/tokens.js';

export interface DetailDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Drawer heading, wired to `aria-labelledby`. */
  title: string;
  /** Accessible name for the close control. */
  closeLabel: string;
  /** Optional action row pinned to the bottom. */
  actions?: ReactNode;
  /** Which edge it slides from. */
  side?: 'right' | 'left';
  children: ReactNode;
  className?: string;
}

/**
 * Slide-over detail panel.
 *
 * A modal dialog: it traps Tab, closes on Escape and on overlay click, and
 * restores focus to whatever opened it.
 *
 * **Portalled to `document.body`, and it has to be.** This used to render
 * inline, on the reasoning that `position: fixed` escapes its container anyway
 * — which is true of *layout* and false of *stacking*. A fixed element is still
 * confined to the nearest ancestor stacking context, so `z-40` is only 40 within
 * whatever that ancestor got. `bp-animate-fade-in` on every surface wrapper is
 * one: it animates `opacity` from 0, and an element under `opacity: 1` creates a
 * stacking context for as long as it lasts. A drawer opened inside that window
 * paints *under* `AppShell`'s `z-20` header — the header stays crisp while the
 * page behind it dims, which is the symptom, and no z-index on either element
 * fixes it because it is not a z-index problem.
 *
 * At `document.body` there is no ancestor left to be trapped by. Testing Library
 * queries `document.body`, so `screen` and `within(document.body)` reach it
 * exactly as before; only queries scoped to `render`'s own container would not,
 * and nothing scopes that way.
 */
export function DetailDrawer({
  open,
  onClose,
  title,
  closeLabel,
  actions,
  side = 'right',
  children,
  className,
}: DetailDrawerProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  /*
    Tab containment, Escape, and focus restoration are `useFocusTrap`'s — the
    same hook `ConsentDialog` uses. They were inline here until a second dialog
    needed them; Article 3's "if you find two, delete one" is why they moved
    rather than being copied.
  */
  useFocusTrap(open, panelRef, onClose);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-40 flex"
      style={{ justifyContent: side === 'right' ? 'flex-end' : 'flex-start' }}
    >
      <div
        data-testid="detail-drawer-overlay"
        onClick={onClose}
        className="absolute inset-0"
        style={{ background: token('bp-scrim') }}
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          'relative z-10 flex h-full w-full max-w-md flex-col border shadow-xl outline-none',
          className,
        )}
        style={{
          background: token('bp-card'),
          borderColor: token('bp-border'),
          boxShadow: token('shadow-md'),
          color: token('bp-text'),
        }}
      >
        <div
          className="flex shrink-0 items-center justify-between gap-4 border-b px-4 py-3"
          style={{ borderColor: token('bp-border') }}
        >
          <h2 id={titleId} className="truncate text-base font-semibold">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={closeLabel}
            className="rounded p-1 text-lg leading-none focus-visible:outline-2"
            style={{ color: token('bp-text-muted'), outlineColor: token('bp-blue-rail') }}
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-4 py-3">{children}</div>

        {actions ? (
          <div
            className="flex shrink-0 items-center justify-end gap-2 border-t px-4 py-3"
            style={{ borderColor: token('bp-border') }}
          >
            {actions}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
